import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AlertAskDeoContext } from 'app/shared/models/alert-ask-deo-context.model';
import { ChatSession } from 'app/shared/screens/deo-ai-assistant/models/chat.models';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface ArtifactPreviewItem {
  id?: string;
  title: string;
  type: string;
  htmlContent: string;
  createdAt?: Date | string;
}

@Injectable({
  providedIn: 'root'
})
export class DEOAiAssistantService {
  private readonly _isOpen = new BehaviorSubject<boolean>(false);
  private readonly _currentSession = new BehaviorSubject<ChatSession | null>(null);
  private readonly _artifactExtensionMap: Record<string, string> = {
    html: 'html',
    svg: 'svg',
    markdown: 'md',
    md: 'md',
    text: 'txt',
    txt: 'txt',
    javascript: 'js',
    js: 'js',
    typescript: 'ts',
    ts: 'ts',
    css: 'css',
    python: 'py',
    py: 'py',
    java: 'java',
    json: 'json',
    xml: 'xml',
    sql: 'sql',
    shell: 'sh',
    bash: 'sh',
    csv: 'csv',
    csharp: 'cs',
    cs: 'cs',
    pdf: 'pdf'
  };

  private readonly _mimeTypeMap: Record<string, string> = {
    html: 'text/html;charset=utf-8',
    svg: 'image/svg+xml;charset=utf-8',
    json: 'application/json;charset=utf-8',
    xml: 'application/xml;charset=utf-8',
    csv: 'text/csv;charset=utf-8',
    md: 'text/markdown;charset=utf-8',
    txt: 'text/plain;charset=utf-8',
    js: 'text/javascript;charset=utf-8',
    ts: 'text/plain;charset=utf-8',
    css: 'text/css;charset=utf-8',
    py: 'text/x-python;charset=utf-8',
    java: 'text/x-java-source;charset=utf-8',
    sql: 'application/sql;charset=utf-8',
    sh: 'application/x-sh;charset=utf-8',
    cs: 'text/plain;charset=utf-8',
    pdf: 'application/pdf'
  };

  public readonly isOpen$: Observable<boolean> = this._isOpen.asObservable();

  private readonly _alertContext = new Subject<AlertAskDeoContext>();

  /**
   * The anomaly a reviewer wants to talk about, pushed by the Alerts tray's Ask DEO.
   *
   * A `Subject`, not a `BehaviorSubject`: it is a one-shot intent, and replaying it would prefill
   * the composer with a stale question the next time the widget is created.
   */
  public readonly alertContext$: Observable<AlertAskDeoContext> = this._alertContext.asObservable();

  /**
   * The agent the widget's "Talking to" selector is set to, or null for DEO itself.
   *
   * Held here rather than on the widget because the widget is destroyed and rebuilt on a trip to
   * `/deo-ai-assist` and back, and a planner should not silently find themselves talking to someone
   * else afterwards. Set both by Ask DEO and by the selector.
   */
  private _selectedAgentId: string | null = null;

  /**
   * Whether a full-page DEO chat is on screen. The floating widget is not rendered under
   * `/deo-ai-assist` (see app.component), so on those routes it is the page that answers an Ask DEO.
   *
   * Set by the page that subscribes to `alertContext$`, so the claim and the handler cannot drift.
   */
  private _isPageChatMounted = false;

  /**
   * A question kept for the assistant screen, set by a page that cannot answer it itself.
   *
   * `alertContext$` is a one-shot `Subject`, so it reaches nobody across a navigation: the project
   * folder view hands the anomaly over and goes to the screen, which is created only afterwards.
   * Only that hand-over sets this — the widget answers its own context, so nothing is left behind to
   * re-open a stale alert later.
   */
  private _pendingAlertContext: AlertAskDeoContext | null = null;

  public isArtifactDrawerVisible = false;
  public selectedArtifact: ArtifactPreviewItem | null = null;
  public artifactView: 'code' | 'preview' = 'preview';
  public safeArtifactHtml: SafeHtml | null = null;

  private static readonly SELF_HOSTED_CHART_JS_PATH = '/assets/vendor/chart.umd.js';

  private static readonly DEFAULT_ARTIFACT_EXTENSION = 'html';

  /**
   * Matches a <script src="..."> pointing at Chart.js on a public CDN, whatever the version or file
   * name (chart.js, chart.umd.min.js, Chart.min.js). The preview iframe inherits the app policy,
   * which allows scripts from this origin only, so those are pointed at the copy the build ships
   * from node_modules.
   */
  private static readonly CDN_CHART_JS_PATTERN =
    /(<script\b[^>]*\bsrc\s*=\s*["'])[^"']*(?:cdn\.jsdelivr\.net|unpkg\.com|cdnjs\.cloudflare\.com)[^"']*chart[^"']*\.js[^"']*(["'])/gi;

  constructor(private readonly _sanitizer: DomSanitizer) {}

  /**
   * Opens the chat agent
   */
  openChat(): void {
    if (!this._currentSession.value) {
      //   this.startNewSession();
    }
    this._isOpen.next(true);
  }

  /** Tells the service a full-page DEO chat is mounted, and is the one that will answer an Ask DEO. */
  setPageChatMounted(isMounted: boolean): void {
    this._isPageChatMounted = isMounted;
  }

  /**
   * Opens the chat on a specific anomaly, carrying its owning agent.
   *
   * The context goes out before the chat opens so a widget that is created by this call already has
   * its opening question when it renders. With a page chat mounted nothing is opened: that page
   * answers the question itself, and opening a widget it hides would only drop it.
   */
  askAboutAlert(context: AlertAskDeoContext): void {
    this._selectedAgentId = context.agentId;
    this._alertContext.next(context);

    if (!this._isPageChatMounted) {
      this.openChat();
    }
  }

  /** Keeps an anomaly for the assistant screen, which is not mounted yet. */
  deferAlertContext(context: AlertAskDeoContext): void {
    this._pendingAlertContext = context;
  }

  /** The handed-over anomaly, once. It is cleared as it is read, so it cannot be answered twice. */
  takePendingAlertContext(): AlertAskDeoContext | null {
    const pending = this._pendingAlertContext;
    this._pendingAlertContext = null;
    return pending;
  }

  /** The agent the selector is on, or null when the conversation is with DEO itself. */
  getSelectedAgentId(): string | null {
    return this._selectedAgentId;
  }

  /** Records a pick from the "Talking to" selector; `null` puts the conversation back to DEO. */
  setSelectedAgentId(agentId: string | null): void {
    this._selectedAgentId = agentId;
  }

  /**
   * Closes the chat agent via X button - this ends the current session
   */
  closeChat(): void {
    this._isOpen.next(false);
    // this.endCurrentSession();
  }

  /**
   * Collapses the chat agent (clicking outside) - preserves the session
   */
  collapseChat(): void {
    this._isOpen.next(false);
    // this.saveSession();
  }

  openArtifactPreview(artifact: ArtifactPreviewItem): void {
    this.selectedArtifact = artifact;
    this.artifactView = 'preview';
    this.safeArtifactHtml = this.buildSafeArtifactHtml(artifact.htmlContent);
    this.isArtifactDrawerVisible = true;
  }

  closeArtifactPreview(): void {
    this.isArtifactDrawerVisible = false;
    this.selectedArtifact = null;
    this.safeArtifactHtml = null;
  }

  changeArtifactView(view: 'code' | 'preview'): void {
    this.artifactView = view;
  }

  downloadArtifactContent(fileName: string, content: string, type?: string): void {
    if (!content) {
      return;
    }

    const extension = this.resolveArtifactExtension(fileName, type);
    const resolvedFileName = this.buildArtifactFileName(fileName, extension);
    const blob = new Blob([content], { type: this.getMimeType(extension) });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');

    anchor.href = url;
    anchor.download = resolvedFileName;
    anchor.click();

    URL.revokeObjectURL(url);
  }

  /**
   * The drawer previews every artifact as an HTML document, so one whose title and type name no format is
   * saved as HTML: an extension-less name on a text/plain blob is what had Chrome saving them as `.txt`
   * (PLAN-2332).
   *
   * A type `_artifactExtensionMap` knows is an explicit format decision and stands as it is. A title is a
   * guess, so an extension read off one counts only when it is a format with a MIME type here — otherwise
   * "Sales 2026.08" is saved as an `.08` file.
   */
  private resolveArtifactExtension(fileName: string, type?: string): string {
    const extensionFromName = this.extractKnownFileExtension(this.sanitizeArtifactFileName(fileName));

    return extensionFromName ?? this.getExtensionFromType(type) ?? DEOAiAssistantService.DEFAULT_ARTIFACT_EXTENSION;
  }

  private getExtensionFromType(type?: string): string | null {
    const normalizedType = type?.trim().toLowerCase();
    if (!normalizedType) {
      return null;
    }

    if (normalizedType.startsWith('.')) {
      return normalizedType.substring(1);
    }

    if (normalizedType.includes('/')) {
      const mimeSubtype = normalizedType.split('/')[1]?.split(';')[0]?.trim();
      return mimeSubtype ? this._artifactExtensionMap[mimeSubtype] || null : null;
    }

    return this._artifactExtensionMap[normalizedType] || null;
  }

  private extractFileExtension(fileName: string): string | null {
    const extensionMatch = fileName.match(/\.([a-z0-9]+)$/i);
    return extensionMatch ? extensionMatch[1].toLowerCase() : null;
  }

  private extractKnownFileExtension(fileName: string): string | null {
    const extension = this.extractFileExtension(fileName);
    return extension && this.isKnownExtension(extension) ? extension : null;
  }

  private isKnownExtension(extension: string): boolean {
    return !!this._mimeTypeMap[extension];
  }

  private buildArtifactFileName(fileName: string, extension: string): string {
    const sanitizedFileName = this.sanitizeArtifactFileName(fileName);
    const normalizedExtension = `.${extension}`;

    if (sanitizedFileName.toLowerCase().endsWith(normalizedExtension)) {
      return sanitizedFileName;
    }

    return `${sanitizedFileName}${normalizedExtension}`;
  }

  private sanitizeArtifactFileName(fileName: string): string {
    const trimmedFileName = fileName?.trim() || 'artifact';
    return trimmedFileName.replace(/[\\/:*?"<>|]/g, '').trim() || 'artifact';
  }

  private buildSafeArtifactHtml(content: string): SafeHtml {
    const safeSrcdoc = this.escapeForHtmlAttribute(this.useSelfHostedChartJs(content));

    /**
     * The artifact runs its own scripts in here. A srcdoc document inherits the embedding page's
     * Content-Security-Policy, so this works only because that policy allows 'unsafe-inline'
     * scripts; the artifact's chart code is generated per request and can carry neither a hash nor
     * a nonce. Chart.js is loaded from this origin, which the same inherited policy permits.
     *
     * The iframe sandbox is what keeps that code away from the Angular app:
     *  Cannot access localStorage
     *  Cannot access window
     *  Cannot navigate parent
     *  Cannot read Angular DOM
     *  Not inserting the HTML directly into Angular DOM, instead embedding it isolated.
     *
     * With inline scripts permitted app-wide, the escaping below is the boundary that keeps the
     * artifact inside the attribute, so it must stay correct. Sanitizing instead is not an option:
     * it would strip the <style> and <script> the artifact needs (TS:S6268).
     */
    return this._sanitizer.bypassSecurityTrustHtml(`
      <iframe
        sandbox="allow-scripts "
        style="width: 100%; height: 100%; border: none; border-radius: 6px; background: white; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);"
        class="artifact-iframe"
        srcdoc="${safeSrcdoc}"
      ></iframe>
    `);
  }

  /**
   * Escapes ampersands before quotes, so an artifact containing `&quot;` cannot be decoded back
   * into a real quote and break out of the srcdoc attribute.
   */
  private escapeForHtmlAttribute(content: string): string {
    return content.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  /**
   * Points Chart.js at the copy served from this origin. Public CDNs are blocked by the app policy
   * the preview iframe inherits, and the artifact would otherwise render a chart-less page.
   */
  private useSelfHostedChartJs(content: string): string {
    return content.replace(DEOAiAssistantService.CDN_CHART_JS_PATTERN, `$1${DEOAiAssistantService.SELF_HOSTED_CHART_JS_PATH}$2`);
  }

  private getMimeType(extension: string): string {
    return this._mimeTypeMap[extension] || 'text/plain;charset=utf-8';
  }
}
